import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Toolbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Snackbar,
  ListItemText
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as ExportIcon,
  Visibility as ViewIcon,
  Sort as SortIcon,
  LocalOffer as LocalOfferIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Order, OrderFilters, OrderSortConfig, OrderSummary, PaymentMethod, DeliveryMethod } from '../models/Order';
import { DateRange } from '../models/CustomerStats';
import orderService from '../services/orderService';
import OrderUtils from '../utils/orderUtils';
import DateUtils from '../utils/dateUtils';
import DateRangePicker from '../components/DateRangePicker';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import OrderDetailsDialog from '../components/OrderDetailsDialog';
import CustomerProfileDialog from '../components/CustomerProfileDialog';
import type { Customer } from '../models/Customer';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { describeActiveScope, filterOrdersByStoreScope, resolveScopedStoreIds } from '../utils/adminScopeUtils';

const computeSummaryStats = (orders: Order[]) => {
  const excludedStatuses = new Set(['CANCELLED', 'NO_DRIVER_FOUND', 'PAYMENT_PENDING', 'PAYMENT_FAILED']);
  const totalOrders = orders.length;
  const revenueOrders = orders.filter((order) => !excludedStatuses.has(order.status));
  const totalRevenue = revenueOrders.reduce((sum, order) => sum + order.totalAmount, 0);
  const averageOrderValue = revenueOrders.length > 0 ? totalRevenue / revenueOrders.length : 0;

  return { totalOrders, totalRevenue, averageOrderValue };
};

type OrdersPageCacheEntry = {
  key: string;
  orders: Order[];
};

let ordersPageCache: OrdersPageCacheEntry | null = null;

const Orders: React.FC = () => {
  const {
    loading: scopeLoading,
    activeScopeType,
    selectedGroupId,
    selectedStoreId,
    selectedGroup,
    selectedStore,
    stores,
  } = useAdminScope();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoOnly, setPromoOnly] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(DateUtils.getTodayRange());
  const [sortConfig, setSortConfig] = useState<OrderSortConfig>({
    field: 'createdAt',
    direction: 'desc'
  });

  // Nouveaux filtres
  const [showCancelledOnly, setShowCancelledOnly] = useState(false);
  const [paymentMethodsFilter, setPaymentMethodsFilter] = useState<PaymentMethod[]>([]);
  const [deliveryMethodsFilter, setDeliveryMethodsFilter] = useState<DeliveryMethod[]>([]);
  
  // États pour la suppression
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderSummariesForDeletion, setOrderSummariesForDeletion] = useState<OrderSummary[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [preparingDeletion, setPreparingDeletion] = useState(false);
  const [expectedDeleteCount, setExpectedDeleteCount] = useState<number | undefined>(undefined);
  
  // États pour les détails d'ordre
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState<Order | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState<Customer | null>(null);
  
  // États pour les notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ totalOrders: 0, totalRevenue: 0, averageOrderValue: 0 });
  const scopedStoreIds = useMemo(
    () =>
      resolveScopedStoreIds({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStoreId, stores]
  );
  const scopeSummary = useMemo(
    () =>
      describeActiveScope({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        stores,
      }),
    [activeScopeType, selectedGroupId, selectedStoreId, stores]
  );
  const isScopedMode = scopedStoreIds !== null;
  const loadRequestIdRef = useRef(0);
  const sortedScopedStoreIds = useMemo(
    () => (scopedStoreIds === null ? null : [...scopedStoreIds].sort()),
    [scopedStoreIds]
  );
  const isScopeReady = useMemo(() => {
    if (scopeLoading) {
      return false;
    }

    if (activeScopeType === 'global') {
      return true;
    }

    return Boolean(selectedStoreId || selectedGroupId) || stores.length === 0;
  }, [scopeLoading, activeScopeType, selectedStoreId, selectedGroupId, stores.length]);
  const ordersCacheKey = useMemo(
    () =>
      JSON.stringify({
        activeScopeType,
        selectedGroupId,
        selectedStoreId,
        scopedStoreIds: sortedScopedStoreIds,
        dateStart: dateRange?.start.toISOString() || null,
        dateEnd: dateRange?.end.toISOString() || null,
        showCancelledOnly,
        paymentMethods: [...paymentMethodsFilter].sort(),
        deliveryMethods: [...deliveryMethodsFilter].sort(),
        promoCode: promoCode.trim(),
      }),
    [
      activeScopeType,
      selectedGroupId,
      selectedStoreId,
      sortedScopedStoreIds,
      dateRange,
      showCancelledOnly,
      paymentMethodsFilter,
      deliveryMethodsFilter,
      promoCode,
    ]
  );

  // Charger statistiques exactes pour la période (requête séparée, indépendante de la pagination/liste)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);

        const shouldComputeLocally = promoOnly || isScopedMode;

        if (shouldComputeLocally) {
          let list = [...orders];
          if (showCancelledOnly) {
            list = list.filter(o => o.status === 'CANCELLED');
          }
          if (paymentMethodsFilter.length > 0) {
            list = list.filter(o => paymentMethodsFilter.includes(o.paymentMethod));
          }
          if (deliveryMethodsFilter.length > 0) {
            list = list.filter(o => deliveryMethodsFilter.includes(o.deliveryMethod));
          }
          if (promoCode.trim().length > 0) {
            list = list.filter(o => (o.promoCodeApplied || '').trim() === promoCode.trim());
          }
          if (promoOnly) {
            list = list.filter(o => (o.promoAmountApplied ?? 0) > 0);
          }

          setStats(computeSummaryStats(list));
          return;
        }

        // Sinon, utiliser la requête stats côté serveur pour exactitude/perfs
        const filters: OrderFilters = {};
        if (dateRange) {
          filters.dateRange = dateRange;
        }
        if (showCancelledOnly) {
          filters.status = ['CANCELLED'];
        }
        if (deliveryMethodsFilter.length > 0) {
          filters.deliveryMethod = deliveryMethodsFilter;
        }
        if (promoCode.trim().length > 0) {
          filters.promoCode = promoCode.trim();
        }
        const s = await orderService.getOrderStats(filters);
        setStats({
          totalOrders: s.totalOrders,
          totalRevenue: s.totalRevenue,
          averageOrderValue: s.averageOrderValue,
        });
      } catch (e) {
        console.error('Erreur lors du chargement des statistiques:', e);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [orders, dateRange, showCancelledOnly, paymentMethodsFilter, deliveryMethodsFilter, promoCode, promoOnly, isScopedMode, selectedStoreId]);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadOrders = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    const cachedOrders = ordersPageCache?.key === ordersCacheKey ? ordersPageCache.orders : null;

    try {
      if (cachedOrders) {
        setOrders(cachedOrders);
        setLoading(false);
        setRefreshing(true);
      } else {
        setLoading(true);
        setRefreshing(false);
      }

      if (!isScopeReady) {
        return;
      }

      if (isScopedMode && scopedStoreIds !== null && scopedStoreIds.length === 0) {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        setOrders([]);
        ordersPageCache = { key: ordersCacheKey, orders: [] };
        return;
      }

      const filters: OrderFilters = {};
      if (dateRange) {
        filters.dateRange = dateRange;
      }
      if (showCancelledOnly) {
        filters.status = ['CANCELLED'];
      }
      if (paymentMethodsFilter.length > 0) {
        filters.paymentMethod = paymentMethodsFilter;
      }
      if (deliveryMethodsFilter.length > 0) {
        filters.deliveryMethod = deliveryMethodsFilter;
      }
      if (promoCode.trim().length > 0) {
        filters.promoCode = promoCode.trim();
      }
      if (selectedStoreId) {
        filters.storeId = selectedStoreId;
      }
      const data = await orderService.getOrders(filters);
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      const scopedOrders = filterOrdersByStoreScope(data, scopedStoreIds);
      ordersPageCache = { key: ordersCacheKey, orders: scopedOrders };
      setOrders(scopedOrders);
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      console.error('Erreur lors du chargement des ordres:', error);
      showSnackbar('Erreur lors du chargement des ordres', 'error');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [
    dateRange,
    deliveryMethodsFilter,
    isScopeReady,
    isScopedMode,
    paymentMethodsFilter,
    promoCode,
    ordersCacheKey,
    scopedStoreIds,
    selectedStoreId,
    showCancelledOnly,
    showSnackbar,
  ]);

  // Charger les ordres
  useEffect(() => {
    if (!isScopeReady) {
      return;
    }

    void loadOrders();
  }, [loadOrders, isScopeReady]);

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = [...orders];

    if (searchTerm) {
      filtered = OrderUtils.filterBySearchTerm(filtered, searchTerm);
    }

    if (dateRange) {
      filtered = filtered.filter(order =>
        DateUtils.isDateInRange(order.createdAt, dateRange)
      );
    }

    if (showCancelledOnly) {
      filtered = filtered.filter(order => order.status === 'CANCELLED');
    }
    if (paymentMethodsFilter.length > 0) {
      filtered = filtered.filter(order => paymentMethodsFilter.includes(order.paymentMethod));
    }
    if (deliveryMethodsFilter.length > 0) {
      filtered = filtered.filter(order => deliveryMethodsFilter.includes(order.deliveryMethod));
    }

    if (promoOnly) {
      filtered = filtered.filter(o => (o.promoAmountApplied ?? 0) > 0);
    }

    filtered = orderService.sortOrders(filtered, sortConfig);

    setFilteredOrders(filtered);
  }, [orders, searchTerm, dateRange, sortConfig, showCancelledOnly, paymentMethodsFilter, deliveryMethodsFilter, promoOnly, promoCode]);

  const handleSelectOrder = (orderId: string) => {
    const newSelection = new Set(selectedOrders);
    if (newSelection.has(orderId)) {
      newSelection.delete(orderId);
    } else {
      newSelection.add(orderId);
    }
    setSelectedOrders(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(order => order.id)));
    }
  };

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field: field as any,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDeleteSelected = async () => {
    if (selectedOrders.size === 0) return;

    try {
      // Ouvrir immédiatement le dialogue et afficher un état de chargement
      setExpectedDeleteCount(selectedOrders.size);
      setOrderSummariesForDeletion([]);
      setDeleteDialogOpen(true);
      setPreparingDeletion(true);

      // Récupérer les résumés des ordres à supprimer en parallèle
      const orderIds = Array.from(selectedOrders);
      const summaries = await Promise.all(orderIds.map(id => orderService.getOrderSummary(id)));
      setOrderSummariesForDeletion(summaries);
    } catch (error) {
      console.error('Erreur lors de la préparation de la suppression:', error);
      showSnackbar('Erreur lors de la préparation de la suppression', 'error');
    } finally {
      setPreparingDeletion(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const orderIds = Array.from(selectedOrders);
      const result = await orderService.deleteMultipleOrders(orderIds);
      
      if (result.success) {
        showSnackbar(`${result.deletedCount} ordre(s) supprimé(s) avec succès`, 'success');
        setSelectedOrders(new Set());
        await loadOrders();
      } else {
        showSnackbar(
          `${result.deletedCount} ordre(s) supprimé(s), ${result.failedCount} échec(s)`, 
          'warning'
        );
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showSnackbar('Erreur lors de la suppression des ordres', 'error');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setPreparingDeletion(false);
      setExpectedDeleteCount(undefined);
    }
  };

  const handleExport = () => {
    const ordersToExport = selectedOrders.size > 0 
      ? filteredOrders.filter(order => selectedOrders.has(order.id))
      : filteredOrders;
    
    const csv = orderService.exportOrdersToCSV(ordersToExport);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSnackbar(`${ordersToExport.length} ordre(s) exporté(s)`, 'success');
  };

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrderForDetails(order);
    setOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedOrderForDetails(null);
  };

  const isAllSelected = selectedOrders.size === filteredOrders.length && filteredOrders.length > 0;
  const isIndeterminate = selectedOrders.size > 0 && selectedOrders.size < filteredOrders.length;

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Ordres
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Scope actif: {scopeSummary}
        {selectedGroup ? ` · Groupe ${selectedGroup.name}` : ''}
        {selectedStore ? ` · Magasin ${selectedStore.name}` : ''}
      </Alert>

      {refreshing ? <LinearProgress sx={{ mb: 2, borderRadius: 999 }} /> : null}

      {/* Filtres */}
      <DateRangePicker
        value={dateRange}
        onChange={setDateRange}
        label="Filtrer par période"
        includeTime
        includeSalesDay
      />

      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showCancelledOnly}
                onChange={(e) => setShowCancelledOnly(e.target.checked)}
              />
            }
            label="Annulées uniquement"
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="payment-filter-label">Méthode de paiement</InputLabel>
            <Select
              labelId="payment-filter-label"
              multiple
              value={paymentMethodsFilter}
              label="Méthode de paiement"
              onChange={(e) => setPaymentMethodsFilter(e.target.value as PaymentMethod[])}
              renderValue={(selected) => (selected as PaymentMethod[]).map(m => OrderUtils.getPaymentMethodLabel(m)).join(', ')}
            >
              {(['cash', 'card'] as PaymentMethod[]).map((m) => (
                <MenuItem key={m} value={m}>
                  <Checkbox checked={paymentMethodsFilter.indexOf(m) > -1} />
                  <ListItemText primary={OrderUtils.getPaymentMethodLabel(m)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="delivery-filter-label">Méthode de livraison</InputLabel>
            <Select
              labelId="delivery-filter-label"
              multiple
              value={deliveryMethodsFilter}
              label="Méthode de livraison"
              onChange={(e) => setDeliveryMethodsFilter(e.target.value as DeliveryMethod[])}
              renderValue={(selected) => (selected as DeliveryMethod[]).map(m => OrderUtils.getDeliveryMethodLabel(m)).join(', ')}
            >
              {(['delivery', 'pickup'] as DeliveryMethod[]).map((m) => (
                <MenuItem key={m} value={m}>
                  <Checkbox checked={deliveryMethodsFilter.indexOf(m) > -1} />
                  <ListItemText primary={OrderUtils.getDeliveryMethodLabel(m)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Code promo"
            placeholder="EX: CODE10"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            sx={{ width: 180 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={promoOnly}
                onChange={(e) => setPromoOnly(e.target.checked)}
              />
            }
            label="Avec code promo uniquement"
          />
          <Box sx={{ flexGrow: 1 }} />

          {selectedOrders.size > 0 && (
            <>
              <Button
                variant="contained"
                color="error"
                startIcon={preparingDeletion ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                onClick={handleDeleteSelected}
                disabled={deleting || preparingDeletion}
              >
                Supprimer ({selectedOrders.size})
              </Button>
            </>
          )}

          <Button
            variant="outlined"
            startIcon={loading || refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={() => void loadOrders()}
            disabled={loading || refreshing}
          >
            Actualiser
          </Button>

          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Exporter
          </Button>
        </Toolbar>
      </Paper>

      {/* Statistiques rapides */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Statistiques
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" color="text.secondary">
              Commandes (période sélectionnée)
            </Typography>
            <Typography variant="h4" color="primary">
              {statsLoading ? <CircularProgress size={20} /> : stats.totalOrders}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" color="text.secondary">
              Chiffre d'affaires (période)
            </Typography>
            <Typography variant="h4" color="success.main">
              {statsLoading ? '...' : OrderUtils.formatAmount(stats.totalRevenue)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" color="text.secondary">
              Panier moyen (période)
            </Typography>
            <Typography variant="h4" color="info.main">
              {statsLoading ? '...' : OrderUtils.formatAmount(stats.averageOrderValue)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tableau des ordres */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('id')}
                  endIcon={<SortIcon />}
                >
                  ID
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('clientName')}
                  endIcon={<SortIcon />}
                >
                  Client
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('products')}
                  endIcon={<SortIcon />}
                >
                  Produits
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('totalAmount')}
                  endIcon={<SortIcon />}
                >
                  Montant
                </Button>
              </TableCell>
              <TableCell>
                Promo
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('paymentMethod')}
                  endIcon={<SortIcon />}
                >
                  Paiement
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('deliveryMethod')}
                  endIcon={<SortIcon />}
                >
                  Livraison
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('status')}
                  endIcon={<SortIcon />}
                >
                  Statut
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('createdAt')}
                  endIcon={<SortIcon />}
                >
                  Date
                </Button>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow key={order.id} hover onClick={() => handleViewOrderDetails(order)} sx={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedOrders.has(order.id)}
                    onChange={() => handleSelectOrder(order.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    #{order.id.slice(-8)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {order.clientName || 'Client inconnu'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.clientPhone || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {OrderUtils.getProductsDescription(order)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {OrderUtils.formatAmount(order.totalAmount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {order.promoAmountApplied > 0 ? (
                    <Chip
                      icon={<LocalOfferIcon fontSize="small" />}
                      label={`${order.promoCodeApplied ? order.promoCodeApplied + ' ' : ''}-${OrderUtils.formatAmount(order.promoAmountApplied)}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={OrderUtils.getPaymentMethodLabel(order.paymentMethod)}
                    size="small"
                    variant="outlined"
                    color={order.paymentMethod === 'cash' ? 'warning' : 'info'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={OrderUtils.getDeliveryMethodLabel(order.deliveryMethod)}
                    size="small"
                    variant="outlined"
                    color={order.deliveryMethod === 'delivery' ? 'primary' : 'secondary'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={OrderUtils.getOrderStatusLabel(order)}
                    size="small"
                    sx={{
                      backgroundColor: OrderUtils.getOrderStatusColor(order),
                      color: 'white'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {DateUtils.formatDateFR(order.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {OrderUtils.getTimeElapsed(order.createdAt)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    title="Voir les détails"
                    onClick={(e) => { e.stopPropagation(); handleViewOrderDetails(order); }}
                  >
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredOrders.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Aucun ordre trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Essayez de modifier vos filtres de recherche
          </Typography>
        </Box>
      )}

      {/* Dialog de confirmation de suppression */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPreparingDeletion(false);
          setExpectedDeleteCount(undefined);
        }}
        onConfirm={confirmDelete}
        orderSummaries={orderSummariesForDeletion}
        loading={deleting}
        preparing={preparingDeletion}
        expectedCount={expectedDeleteCount}
      />

      {/* Dialog de détails d'ordre */}
      <OrderDetailsDialog
        open={orderDetailsOpen}
        onClose={handleCloseOrderDetails}
        order={selectedOrderForDetails}
        onOpenCustomerProfile={(customer) => { setProfileCustomer(customer); setProfileOpen(true); }}
      />

      {/* Dialog profil client */}
      <CustomerProfileDialog
        open={profileOpen}
        customer={profileCustomer}
        onClose={() => { setProfileOpen(false); setProfileCustomer(null); }}
      />

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Orders;
